import { InfoListItem } from '@brightlayer-ui/react-components';
import { Box, Divider, IconButton, Paper, Stack, Switch, Typography } from '@mui/material';
import React, { type JSX } from 'react';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useSortedData } from 'hooks/useSortedData';
import { WidgetHeader } from 'pages/WidgetManagement/common/WidgetHeader';
import { WidgetFooter } from 'pages/WidgetManagement/common/WidgetFooter';
import FormatListBulleted from '@mui/icons-material/FormatListBulleted';
import { CustomTransComponent, GroupPath } from 'components';
import { CustomEmptyState } from 'components/CustomEmptyState';
import { ChevronRight } from '@mui/icons-material';
import { useRBAC } from 'hooks';
import { useTypedSelector } from '@fiji/common';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';
const loadNumberStyle = {
    fontSize: '14px',
    fontWeight: '600',
    backgroundColor: '#727E84',
    color: '#fff',
    width: '18px',
    height: '18px',
    borderRadius: '4px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
};

const Component = ({
    widgetData: detailWidget,
    mode,
    loadsHandler,
}: {
    widgetData: any;
    mode: string;
    loadsHandler: any;
}): JSX.Element => {
    const { sortedData, ids } = useSortedData(detailWidget ?? [], ['primary', 'secondary'], 'ASC', 'loads');
    const navigate = useNavigate();
    const theme: any = useTheme();

    const permissions = useTypedSelector(selectCurrentPermission);
    const { hasPermission } = useRBAC(permissions);
    const canUpdateDevice = hasPermission('device-loads_edit-devices');
    const canViewDevice = hasPermission('device-loads_view-devices');
    const tabsData = useTypedSelector((state: any) => state.common.tabsList);

    const getLoads = (): any => {
        if (sortedData?.secondary?.length) {
            return sortedData?.secondary?.filter((item: any) => item?.isLoadEnabledInMap);
        }
        return [];
    };

    return (
        <Paper sx={{ height: '100%' }}>
            <WidgetHeader
                mode={mode}
                widgetData={detailWidget}
                sortedData={sortedData}
                count={sortedData?.secondary?.length}
            />
            {mode !== 'edit' && <Divider />}
            <Stack
                sx={{
                    height: `calc(100% - ${mode === 'edit' || detailWidget?.viewAllButton ? '97px' : '49px'})`,
                    overflow: 'auto',
                }}
            >
                {getLoads().length ? (
                    getLoads()
                        .slice(0, detailWidget?.maxCount ?? getLoads().length)
                        .map((item: any) => (
                            <InfoListItem
                                title={''}
                                key={item?.id ?? item?.['loadNumber']}
                                divider="full"
                                leftComponent={
                                    <Stack className="white-space-pre">
                                        <Typography sx={{ fontSize: '16px', fontWeight: '600', color: '#424E54' }}>
                                            {item?.['loadName']}
                                        </Typography>
                                        {detailWidget?.multiDevice && (
                                            <GroupPath
                                                deviceName={item?.deviceName}
                                                pathName={item?.groupPath}
                                                width={'220px'}
                                            />
                                        )}
                                    </Stack>
                                }
                                icon={<Box sx={loadNumberStyle}>{parseInt(item?.['loadNumber'] ?? 0) + 1}</Box>}
                                rightComponent={
                                    <Stack
                                        sx={{
                                            flexDirection: 'row !important',
                                        }}
                                    >
                                        <Switch
                                            checked={item?.isLoadEnabled}
                                            disabled={
                                                !item?.isLoadEnabledInMap ||
                                                item?.offlineDevice ||
                                                mode !== 'view' ||
                                                !canUpdateDevice
                                            }
                                            onClick={(e: any): void => {
                                                e?.stopPropagation();
                                                loadsHandler?.({
                                                    ...item,
                                                });
                                            }}
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                    color: theme?.palette?.primary?.main,
                                                    '& + .MuiSwitch-track': {
                                                        backgroundColor: theme?.palette?.primary?.main,
                                                    },
                                                },
                                                '& .MuiSwitch-switchBase.Mui-checked.Mui-disabled': {
                                                    color: theme?.palette?.primary?.[50],
                                                    '& + .MuiSwitch-track': {
                                                        backgroundColor: theme?.palette?.primary?.[200],
                                                    },
                                                },
                                            }}
                                        />
                                        <IconButton
                                            onClick={(): void => {
                                                if (detailWidget?.detailRoute && mode === 'view')
                                                    navigate(
                                                        `${detailWidget?.detailRoute}/${item?.deviceId || ''}/${
                                                            item.loadId
                                                        }`
                                                    );
                                            }}
                                        >
                                            <ChevronRight />
                                        </IconButton>
                                    </Stack>
                                }
                            />
                        ))
                ) : (
                    <CustomEmptyState
                        className="padding-y-96"
                        icon={<FormatListBulleted className="height-76 width-76 color-icon" />}
                        title={(<CustomTransComponent translationKey={'WIDGETS:LOADS.NO_LOADS_FOUND'} />) as any}
                    />
                )}
            </Stack>
            {(canViewDevice || tabsData?.includes('loads')) && (
                <WidgetFooter mode={mode} widgetData={detailWidget} ids={ids} />
            )}
        </Paper>
    );
};

export default Component;
